import type { CampaignStatus } from '~/types/graphql';
import type { ICampaignGroup } from '../types';

import { useCampaignGroupUpdateMutation } from '~/api/graphql/mutations/campaignGroupUpdate.generated';
import { CAMPAIGN_GROUP_MSG } from '../constants';
import { useCampaignGroupStore } from '../stores';
import { useToast } from '~/hooks/useToast';
import { useState } from 'react';

export interface UpdateCampaignGroupStateOptions {
  hideSuccessMessage?: boolean;
}

export interface IUpdateCampaignState<T extends ICampaignGroup> {
  isLoadingUpdateState: boolean;
  handleUpdateCampaignGroupState: (
    data: T,
    options?: UpdateCampaignGroupStateOptions,
  ) => Promise<T | undefined>;
}

export const useMutationCampaignGroupState = <
  T extends ICampaignGroup,
>(): IUpdateCampaignState<T> => {
  const [isLoading, setIsLoading] = useState(false);

  const upsertCampaignGroup = useCampaignGroupStore((state) => state.upsertCampaignGroup);
  const updateCampaignGroupMutation = useCampaignGroupUpdateMutation();

  const { shopifyToast } = useToast();
  // const { handleSyncToLivePage } = useSyncMetafieldCGProductPrice();
  // const { handleSyncToLivePage: handleSyncToLivePageCGTemplate } = useCGTemplateSyncMetafield();

  const handleToastMessage = (
    status: CampaignStatus,
    isSuccess = false,
    hideSuccessMessage?: boolean,
  ) => {
    if (hideSuccessMessage) return;

    const toastMessages: Record<CampaignStatus, any> = {
      COMPLETED: CAMPAIGN_GROUP_MSG.RUNNING,
      RUNNING: CAMPAIGN_GROUP_MSG.RUNNING,
      PAUSED: CAMPAIGN_GROUP_MSG.PAUSED,
      STOPPED: CAMPAIGN_GROUP_MSG.ENDED,
      DRAFT: CAMPAIGN_GROUP_MSG.PAUSED,
    };

    const message = isSuccess ? toastMessages[status].SUCCESS : toastMessages[status].FAILED;
    shopifyToast(message, { isError: !isSuccess });
  };

  const handleUpdateCampaignGroupState = async (
    data: T,
    options?: UpdateCampaignGroupStateOptions,
  ): Promise<T | undefined> => {
    if (!data) return;
    setIsLoading(true);

    try {
      const { campaignGroupUpdate } = await updateCampaignGroupMutation.mutateAsync({
        id: data.id,
        input: {
          status: data.status,
        },
      });
      if (!campaignGroupUpdate) throw new Error('Campaign update failed');

      handleToastMessage(data.status!, true, options?.hideSuccessMessage);
      campaignGroupUpdate.campaigns = data.campaigns || [];
      upsertCampaignGroup(campaignGroupUpdate as T);

      return campaignGroupUpdate as T;
    } catch (error) {
      handleToastMessage(data.status!, false);
      throw new Error((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoadingUpdateState: isLoading,
    handleUpdateCampaignGroupState,
  };
};
